import { useEffect, useRef } from 'react';
import { Card, Sheet } from '@ai-tutor/ui';
import { mockVersions } from '@/data/fixtures';
import { Check, Clock, Loader2, User, RotateCcw } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { useDataMode } from '@/contexts/DataModeContext';
import type { VersionSummary } from '@/lib/api-client';
import { parseUtcDate } from '@/lib/datetime';
import { cn } from '@/lib/utils';
import { useMediaQuery } from '@/hooks/ui/useMediaQuery';

/** Optimistic placeholder inserted by useRegenerateLesson.onMutate. */
type GeneratingVersionSummary = VersionSummary & { _generating?: boolean };

interface VersionSelectorProps {
  currentVersion: string;
  onVersionChange: (version: string) => void;
  onClose: () => void;
  /** Real version data from API (used in api mode) */
  versions?: GeneratingVersionSummary[];
  /** Whether a version switch is in progress */
  isLoading?: boolean;
}

/** Map trigger type to human-readable label */
function getTriggerLabel(trigger: string): string {
  switch (trigger) {
    case 'initial':
      return 'Initial generation';
    case 'source_added':
      return 'Source added';
    case 'source_removed':
      return 'Source removed';
    case 'prompt_edited':
      return 'Prompt edited';
    default:
      return trigger;
  }
}

export function VersionSelector({
  currentVersion,
  onVersionChange,
  onClose,
  versions,
  isLoading,
}: VersionSelectorProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const { mode } = useDataMode();
  // Below `sm` the list is a bottom sheet, not a dropdown. The dropdown sat
  // 2rem left of its trigger - room the desktop panel's back arrow gave it -
  // and in the phone sources sheet, with no back arrow, it ran off the left
  // edge of the screen.
  const isWide = useMediaQuery('(min-width: 640px)');

  useEffect(() => {
    // The dropdown's own outside-tap close. The sheet has a backdrop, and it
    // is portalled outside `containerRef`, so every tap in it would count as
    // "outside" here.
    if (!isWide) return;
    const handleClickOutside = (event: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        onClose();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [onClose, isWide]);

  // Use real versions in API mode, mock data otherwise
  const useRealData = mode === 'api' && versions && versions.length > 0;

  const list = (
    // Scrollable list - the same rows in either frame.
    <div className='flex-1 space-y-1 overflow-y-auto p-2'>
      {useRealData
        ? versions!.map(version => {
            const generating = version._generating === true;
            const isCurrent = version.is_current;
            const clickable = !isLoading && !isCurrent && !generating;
            return (
              <button
                key={version.version}
                type='button'
                aria-current={isCurrent ? 'true' : undefined}
                onClick={
                  clickable ? () => onVersionChange(version.version) : undefined
                }
                disabled={!clickable && !isCurrent}
                className={cn(
                  // Shared shape - same rounded corners, padding, layout
                  // for every item so hover and selected feel like the
                  // same primitive (mirrors the FlowSelector pattern).
                  'block w-full rounded-xl p-3 text-left transition-colors',
                  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-400/60 focus-visible:ring-offset-0 dark:focus-visible:ring-primary/60',
                  isCurrent
                    ? // Selected: indigo wash (same as FlowSelector active item).
                      // No ring, no opacity dim - clearly highlighted, not disabled.
                      'cursor-default bg-brand-50/70 dark:bg-tint-brand'
                    : clickable
                      ? 'hover:bg-muted dark:hover:bg-accent'
                      : // Loading / generating: muted, no hover.
                        'cursor-not-allowed opacity-60'
                )}
              >
                <div className='flex w-full items-start gap-3'>
                  <div className='mt-0.5 flex-shrink-0'>
                    {generating ? (
                      <Loader2 className='h-4 w-4 animate-spin text-amber-500' />
                    ) : isCurrent ? (
                      <Check className='h-4 w-4 text-brand-600 dark:text-primary' />
                    ) : (
                      <RotateCcw className='h-4 w-4 text-muted-foreground' />
                    )}
                  </div>

                  <div className='min-w-0 flex-1'>
                    <div className='mb-1 flex items-center gap-2'>
                      <span
                        className={cn(
                          'text-sm font-medium',
                          isCurrent
                            ? 'text-brand-700 dark:text-tint-brand-foreground'
                            : 'text-foreground'
                        )}
                      >
                        v{version.version}
                      </span>
                      {isCurrent && (
                        <span className='dark:tint-brand rounded-full bg-brand-100 px-2 py-0.5 text-xs font-medium text-brand-700'>
                          Current
                        </span>
                      )}
                      {generating && (
                        <span className='rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-800 dark:bg-amber-900/30 dark:text-amber-400'>
                          Generating…
                        </span>
                      )}
                    </div>

                    <p className='mb-2 line-clamp-2 text-xs text-muted-foreground'>
                      {version.trigger_details ||
                        getTriggerLabel(version.trigger)}
                    </p>

                    {version.created_at && !generating && (
                      <div className='flex items-center gap-2 text-xs text-muted-foreground'>
                        <Clock className='h-3 w-3' />
                        <span>
                          {formatDistanceToNow(
                            parseUtcDate(version.created_at),
                            {
                              addSuffix: true,
                            }
                          )}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              </button>
            );
          })
        : mockVersions.map(version => {
            const isCurrent = version.id === currentVersion;
            return (
              <button
                key={version.id}
                type='button'
                aria-current={isCurrent ? 'true' : undefined}
                onClick={
                  isCurrent ? undefined : () => onVersionChange(version.id)
                }
                className={cn(
                  'block w-full rounded-xl p-3 text-left transition-colors',
                  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-400/60 focus-visible:ring-offset-0 dark:focus-visible:ring-primary/60',
                  isCurrent
                    ? 'cursor-default bg-brand-50/70 dark:bg-tint-brand'
                    : 'hover:bg-muted dark:hover:bg-accent'
                )}
              >
                <div className='flex w-full items-start gap-3'>
                  <div className='mt-0.5 flex-shrink-0'>
                    {isCurrent ? (
                      <Check className='h-4 w-4 text-brand-600 dark:text-primary' />
                    ) : (
                      <Clock className='h-4 w-4 text-muted-foreground' />
                    )}
                  </div>

                  <div className='min-w-0 flex-1'>
                    <div className='mb-1 flex items-center gap-2'>
                      <span
                        className={cn(
                          'text-sm font-medium',
                          isCurrent
                            ? 'text-brand-700 dark:text-tint-brand-foreground'
                            : 'text-foreground'
                        )}
                      >
                        {version.label}
                      </span>
                      {version.isCurrent && (
                        <span className='dark:tint-brand rounded-full bg-brand-100 px-2 py-0.5 text-xs font-medium text-brand-700'>
                          Current
                        </span>
                      )}
                    </div>

                    <p className='mb-2 line-clamp-2 text-xs text-muted-foreground'>
                      {version.changes}
                    </p>

                    <div className='flex items-center gap-2 text-xs text-muted-foreground'>
                      <User className='h-3 w-3' />
                      <span>{version.createdBy}</span>
                      <span>•</span>
                      <span>
                        {formatDistanceToNow(version.createdAt, {
                          addSuffix: true,
                        })}
                      </span>
                    </div>
                  </div>
                </div>
              </button>
            );
          })}
    </div>
  );

  if (!isWide) {
    return (
      <Sheet
        isOpen
        onClose={onClose}
        side='bottom'
        size='auto'
        title='Version history'
      >
        <p className='flex-shrink-0 px-4 pt-3 text-xs text-muted-foreground'>
          Select a version to switch to
        </p>
        {list}
      </Sheet>
    );
  }

  return (
    <>
      {/* Backdrop */}
      <div className='fixed inset-0 z-10' onClick={onClose} />

      {/* Dropdown */}
      <Card
        ref={containerRef}
        className='absolute top-10 z-20 flex max-h-[28rem] w-80 flex-col overflow-hidden rounded-xl border border-border bg-card shadow-xl'
        style={{ left: '-2rem' }}
      >
        {/* Fixed Header */}
        <div className='flex-shrink-0 border-b border-border p-3'>
          <h3 className='text-sm font-medium text-foreground'>
            Version history
          </h3>
          <p className='mt-1 text-xs text-muted-foreground'>
            Select a version to switch to
          </p>
        </div>
        {list}
      </Card>
    </>
  );
}
